import React, { useState, useEffect } from 'react';

const AddSupplier = ({ formId, onSubmit, isSubmitting, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    tel: '',
    adresse: '',
    email: '',
    matriculeFiscal: ''
  });
  
  const [errors, setErrors] = useState({});

  // Use initialData if provided (for editing existing suppliers)
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        tel: initialData.tel || '',
        adresse: initialData.adresse || '',
        email: initialData.email || '',
        matriculeFiscal: initialData.matriculeFiscal || ''
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.tel.trim()) {
      newErrors.tel = 'Le téléphone est requis';
    }
    
    if (!formData.adresse.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.matriculeFiscal.trim()) {
      newErrors.matriculeFiscal = 'Le matricule fiscal est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Nom du fournisseur *
          </label>
          <div className="mt-1 relative">
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`appearance-none block w-full px-4 py-2.5 bg-gray-50 border ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent transition-all duration-200`}
              placeholder="Nom du fournisseur"
              autoFocus
            />
            {errors.name && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.name}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="tel" className="block text-sm font-medium text-gray-700">
            Téléphone *
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">+216</span>
            </div>
            <input
              type="text"
              name="tel"
              id="tel"
              value={formData.tel}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`appearance-none block w-full pl-14 pr-4 py-2.5 bg-gray-50 border ${
                errors.tel ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent transition-all duration-200`}
              placeholder="XX XXX XXX"
            />
            {errors.tel && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.tel}
              </p>
            )}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email *
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`appearance-none block w-full pl-12 pr-4 py-2.5 bg-gray-50 border ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent transition-all duration-200`}
              placeholder="exemple@domaine.com"
            />
            {errors.email && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="matriculeFiscal" className="block text-sm font-medium text-gray-700">
            Matricule Fiscal *
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm font-mono">TN</span>
            </div>
            <input
              type="text"
              name="matriculeFiscal"
              id="matriculeFiscal"
              value={formData.matriculeFiscal}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`appearance-none block w-full pl-12 pr-4 py-2.5 bg-gray-50 border ${
                errors.matriculeFiscal ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent transition-all duration-200`}
              placeholder="12345678"
              maxLength={8}
            />
            {errors.matriculeFiscal && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.matriculeFiscal}
              </p>
            )}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="adresse" className="block text-sm font-medium text-gray-700">
            Adresse *
          </label>
          <div className="mt-1 relative">
            <textarea
              name="adresse"
              id="adresse"
              rows={3}
              value={formData.adresse}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`appearance-none block w-full px-4 py-3 bg-gray-50 border ${
                errors.adresse ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent transition-all duration-200`}
              placeholder="Adresse complète du fournisseur"
            />
            {errors.adresse && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.adresse}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center text-gray-500 text-sm">
        <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Les champs marqués avec * sont obligatoires</span>
      </div>
    </form>
  );
};

export default AddSupplier;